import React, {
  forwardRef,
  useCallback,
  useEffect,
  useRef,
  useState,
  type ComponentPropsWithRef,
  type PropsWithChildren,
} from "react";

import { CommandMan, CommandType, State, type Damper as DamperType } from "@eisox/api/core-api";
import { cn } from "@eisox/design-system";
import { useComposedRefs } from "@radix-ui/react-compose-refs";
import { createContext } from "@radix-ui/react-context";

import WarningIcon from "~/assets/boilerroomWarning.svg?react";
import Commutator from "~/assets/commutator.svg?react";
import { Tooltip } from "~/components/tooltip";
import { m } from "~/paraglide/messages";

import { getActuatorState, isAutoManualOrForcing } from "../helpers";
import { Direction } from "../types";
import { Card } from "./card";

/* -------------------------------------------------------------------------------------------------
 * Provider
 * -----------------------------------------------------------------------------------------------*/

const NAME = "RecirculationDamper";

interface RecirculationDamperContextValue {
  side: Direction;
  alignedEdge?: number;
  flowRefCallback: (node: HTMLDivElement | null) => void;
}

const [RecirculationDamperProvider, useRecirculationDamperContext] =
  createContext<RecirculationDamperContextValue>(NAME);

interface RecirculationDamperProps extends PropsWithChildren {
  side?: Direction;
}

const Root = ({ side = Direction.LEFT, children }: RecirculationDamperProps) => {
  const [alignedEdge, setAlignedEdge] = useState<number>();

  const flowRefCallback = useCallback(
    (node: HTMLDivElement | null) => {
      if (!node) return;
      const { left, right } = node.getBoundingClientRect();
      setAlignedEdge(prev => {
        if (side === Direction.LEFT) return Math.max(prev ?? right, right);
        return Math.min(prev ?? left, left);
      });
    },
    [side],
  );

  return (
    <RecirculationDamperProvider side={side} alignedEdge={alignedEdge} flowRefCallback={flowRefCallback}>
      {children}
    </RecirculationDamperProvider>
  );
};

/* -------------------------------------------------------------------------------------------------
 * Damper
 * -----------------------------------------------------------------------------------------------*/

const DAMPER_NAME = "Damper";

interface DamperProps extends Omit<DamperType, "id">, ComponentPropsWithRef<"div"> {}

const Damper: React.FC<DamperProps> = forwardRef<HTMLDivElement, DamperProps>(
  ({ state: stateProp, command, commandAutomate, commandMan, commandType, defect = false, ...props }, forwardedRef) => {
    const { side, flowRefCallback } = useRecirculationDamperContext(DAMPER_NAME);
    const { flowRef, width } = useFlow({ name: DAMPER_NAME });
    const composedRefs = useComposedRefs(flowRef, forwardedRef, flowRefCallback);

    const { state, warning } = getActuatorState({ state: stateProp, commandMan, commandAutomate, defect });
    const autoManualOrForcing = isAutoManualOrForcing(commandMan, command);

    const opening = (commandType === CommandType.PERCENTAGE ? state : state === State.ON ? 100 : 0) ?? 45;
    const rotationAngle = (opening / 100) * 90;

    return (
      <div
        ref={composedRefs}
        className="h-23.5 relative"
        {...props}
        style={{ width, cursor: props.onClick ? "pointer" : "default" }}
      >
        <div
          className={cn(
            "h-11 w-23.5 absolute bottom-px flex translate-y-full items-center border-r border-l border-primary-dark-gray bg-neutral-100",
            side === Direction.LEFT ? "right-0" : "left-0",
          )}
        >
          <Card
            className="right-0 after:left-0 after:h-0.5 after:w-2.5 absolute translate-x-[calc(100%+9px)] after:absolute after:top-1/2 after:-translate-x-full after:-translate-y-1/2 after:bg-primary-dark-blue after:content-['']"
            defect={defect}
          >
            {commandMan === CommandMan.MANUAL && <Commutator />}
            {commandType === CommandType.PERCENTAGE &&
              `${m.actuator_state({ state: autoManualOrForcing })} - ${opening.toFixed(1)} %`}
            {commandType === CommandType.ALL_OR_NOTHING &&
              `${m.actuator_state({ state: autoManualOrForcing })} - ${state === State.ON ? m.open() : m.closed()}`}
            {warning && (
              <Tooltip content={warning}>
                <WarningIcon />
              </Tooltip>
            )}
          </Card>
          <svg width="93" height="29" viewBox="0 0 93 29" fill="none" xmlns="http://www.w3.org/2000/svg">
            <rect x="0.5" y="0.5" width="92" height="28" rx="5" fill="#F5F4F4" />
            <rect x="0.5" y="0.5" width="92" height="28" rx="5" stroke="#122138" />

            <rect x="0.5" y="13.5" width="92" height="2" fill="#122138" />

            <rect
              x="1.5"
              y="13"
              width="22"
              height="3"
              rx="1.5"
              fill="#122138"
              transform={`rotate(${rotationAngle} 12.5 14.5)`}
            />
            <circle cx="12.5" cy="14.5" r="2.5" fill="#122138" />

            <rect
              x="24.1667"
              y="13"
              width="22"
              height="3"
              rx="1.5"
              fill="#122138"
              transform={`rotate(-${rotationAngle} 35.1667 14.5)`}
            />
            <circle cx="35.1667" cy="14.5" r="2.5" fill="#122138" />

            <rect
              x="46.8333"
              y="13"
              width="22"
              height="3"
              rx="1.5"
              fill="#122138"
              transform={`rotate(${rotationAngle} 57.8333 14.5)`}
            />
            <circle cx="57.8333" cy="14.5" r="2.5" fill="#122138" />

            <rect
              x="69.5"
              y="13"
              width="22"
              height="3"
              rx="1.5"
              fill="#122138"
              transform={`rotate(-${rotationAngle} 80.5 14.5)`}
            />
            <circle cx="80.5" cy="14.5" r="2.5" fill="#122138" />
          </svg>
        </div>
      </div>
    );
  },
);

/* -------------------------------------------------------------------------------------------------
 * Spacer
 * -----------------------------------------------------------------------------------------------*/

const SPACER_NAME = "Spacer";

const Spacer = forwardRef<HTMLDivElement, ComponentPropsWithRef<"div">>((props, forwardedRef) => {
  const { flowRefCallback } = useRecirculationDamperContext(SPACER_NAME);
  const { flowRef, width } = useFlow({ name: SPACER_NAME });
  const composedRefs = useComposedRefs(flowRef, forwardedRef, flowRefCallback);

  return <div ref={composedRefs} className="h-23.5 relative" {...props} style={{ width }} />;
});

interface UseFlowProps {
  name: string;
}

const DEFAULT_WIDTH = 94;

const useFlow = ({ name }: UseFlowProps) => {
  const flowRef = useRef<HTMLDivElement>(null);

  const { side, alignedEdge } = useRecirculationDamperContext(name);
  const [width, setWidth] = useState(DEFAULT_WIDTH);

  useEffect(() => {
    if (!flowRef.current || alignedEdge === undefined) return;
    const { left, right } = flowRef.current.getBoundingClientRect();
    // Return and supply rows are right-aligned: widening grows leftwards, so that side aligns left edges.
    const offset = side === Direction.LEFT ? alignedEdge - right : left - alignedEdge;
    if (offset > 0) setWidth(DEFAULT_WIDTH + offset);
  }, [side, alignedEdge]);

  return { flowRef, width };
};

export { Damper, Root, Spacer };
export type { RecirculationDamperProps };
